// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useState, lazy, Suspense } from 'react'

import { AuthProvider, useAuth } from './auth/AuthContext'
import { ToastProvider, ToastSessionGuard } from './components/Toast'
import { PharmacyProvider, usePharmacy } from './auth/PharmacyContext'
import { ModulesProvider, useModules } from './auth/ModulesContext'
import ModuleLocked from './components/ModuleLocked'
import AppShell from './shell/AppShell'

import RoadmapPage from './pages/RoadmapPage'
import CareRxPage from './pages/CareRxPage'
import SecureSharePage from './pages/SecureSharePage'
import RoadmapDetailPage from './pages/RoadmapDetailPage'
import { PrivacyPolicyPage, TermsOfUsePage, DataRightsPage, AccessibilityStatementPage } from './pages/legal/LegalPages'
import WebhookApiDocsPage from './pages/docs/WebhookApiDocsPage'
import LoginPage from './pages/LoginPage'
import PharmacyLoginPage from './pages/PharmacyLoginPage'
import MarketingLanding from './pages/MarketingLanding'
import { resolvePharmacyFromUrl, isApexMarketingHost } from './auth/pharmacyUrl'
import PharmacyWorkspaceEntry from './pages/PharmacyWorkspaceEntry'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import OwnerHome from './pages/dashboards/OwnerHome'
import SaasHome from './pages/dashboards/SaasHome'
import OperationalDashboard from './pages/dashboards/OperationalDashboard'
import ClinicalDashboard from './pages/dashboards/ClinicalDashboard'
import MonthEndClosePage from './pages/reports/MonthEndClosePage'
import InsightsDashboard from './pages/dashboards/InsightsDashboard'
import ReportsIndex, { ReportDetail, SavedReportDetail, PaByDrugReport } from './pages/reports/ReportsPages'
import ProviderDashboard from './pages/dashboards/ProviderDashboard'
import ManufacturerDashboard from './pages/dashboards/ManufacturerDashboard'
import SupportTicketsPage from './pages/support/SupportTicketsPage'
import SupportQueuePage from './pages/support/SupportQueuePage'
import SupportHomePage from './pages/support/SupportHomePage'
import SupportMonitorPage from './pages/support/SupportMonitorPage'
import SupportAiPage from './pages/support/SupportAiPage'
import SupportSettingsPage from './pages/support/SupportSettingsPage'
import SupportTeamReportPage from './pages/support/SupportTeamReportPage'
import SupportTeamMembersPage from './pages/support/SupportTeamMembersPage'
import SupportNotificationDeliveryPage from './pages/support/SupportNotificationDeliveryPage'
import SupportToolsPage from './pages/support/SupportToolsPage'
import PioneerLookupPage from './pages/support/PioneerLookupPage'
import SupportTicketDetailPage from './pages/support/SupportTicketDetailPage'
import SystemLogsPage from './pages/support/SystemLogsPage'
import ProviderPatientPage from './pages/dashboards/ProviderPatientPage'
import PatientDashboard from './pages/dashboards/PatientDashboard'
import AdminDashboard from './pages/dashboards/AdminDashboard'
import AdminLayout from './pages/admin/AdminLayout'
import RecordingWidget from './components/RecordingWidget'
import PlatformSsoConfig from './pages/admin/PlatformSsoConfig'
import PlatformEmailConfig from './pages/admin/PlatformEmailConfig'
import UsersTab from './pages/admin/UsersTab'
import RolesTab from './pages/admin/RolesTab'
import GroupsTab from './pages/admin/GroupsTab'
import UsersAndGroupsPage from './pages/UsersAndGroupsPage'
import InvitationsTab from './pages/admin/InvitationsTab'
import AuditExplorer from './pages/admin/AuditExplorer'
import DemoResetControl from './pages/admin/DemoResetControl'
import FeedbackTab from './pages/admin/FeedbackTab'
import DsrTab from './pages/admin/DsrTab'
import PioneerRxTab from './pages/admin/PioneerRxTab'
import PharmaciesTab from './pages/admin/PharmaciesTab'
import PharmacyOnboardingWizard from './pages/admin/PharmacyOnboardingWizard'
import PharmacyDetailPage from './pages/admin/PharmacyDetailPage'
import PharmacySettingsHub from './pages/settings/PharmacySettingsHub'
import EsignConfigPage from './pages/carerx/EsignConfigPage'
import DosingCalculatorPage from './pages/carerx/DosingCalculatorPage'
import CareRxPotWorklist from './pages/carerx/CareRxPotWorklist'
// D-POT-2: the POT classic section is code-split — ~170 KB gzip of screens only a nurse/intake user
// opens, so the first paint of every other page is unchanged (E-08 bundle budget).
const PotRoutes = lazy(() => import('./pages/pot/PotRoutes'))
// D-POT-5 §2.3 — the OwlRx Plan of Treatment form (14-step wizard) opened from the native queue / dashboard.
const CareRxPotFormPage = lazy(() => import('./pages/carerx/CareRxPotForm'))
import NotificationSettings from './pages/settings/NotificationSettings'
import ApiKeysAdmin from './pages/admin/ApiKeysAdmin'
import CarrierOrderImportPage from './pages/admin/CarrierOrderImportPage'
import NetworkSecurityTab from './pages/admin/NetworkSecurityTab'
import SecurityDashboardTab from './pages/admin/SecurityDashboardTab'
import ComplianceTab from './pages/admin/ComplianceTab'
import CostTab from './pages/admin/CostTab'
import ProfitabilityPage from './pages/admin/ProfitabilityPage'
import WorkersTab from './pages/admin/WorkersTab'
import ResourcesTab from './pages/admin/ResourcesTab'
import ApiConfigTab from './pages/admin/ApiConfigTab'
import DataApiGovernanceTab from './pages/admin/DataApiGovernanceTab'
import ReportBuilderPage from './pages/admin/ReportBuilderPage'
import ReportBuilderSqlPage from './pages/admin/ReportBuilderSqlPage'
import ReportAiPromptPage from './pages/admin/ReportAiPromptPage'
import RxViewBuilderPage from './pages/admin/RxViewBuilderPage'
import SystemMonitor from './pages/admin/SystemMonitor'
import LatencyBoard from './pages/admin/LatencyBoard'
import PharmacyLayout from './pages/pharmacy/PharmacyLayout'
import ShipmentsPage from './pages/pharmacy/ShipmentsPage'
import LabelPrintView from './pages/pharmacy/LabelPrintView'
import GlobalOrderSearchPage from './pages/pharmacy/GlobalOrderSearchPage'
import CourierCensusPage from './pages/pharmacy/CourierCensusPage'
import ShippingReportPage from './pages/pharmacy/ShippingReportPage'
import PharmacyReportsPage from './pages/pharmacy/PharmacyReportsPage'
import IntakeTab from './pages/pharmacy/IntakeTab'
import InsuranceTab from './pages/pharmacy/InsuranceTab'
import ProductionTab from './pages/pharmacy/ProductionTab'
import ReadyAndOutTab from './pages/pharmacy/ReadyAndOutTab'
import OnHoldTab from './pages/pharmacy/OnHoldTab'
import AllRxTab from './pages/pharmacy/AllRxTab'
import CustomViewTab from './pages/pharmacy/CustomViewTab'
import RefillsTab from './pages/pharmacy/RefillsTab'
import RxDetailPage from './pages/pharmacy/RxDetailPage'
import PatientListPage from './pages/people/PatientListPage'
import PatientDetailPage from './pages/people/PatientDetailPage'
import PrescriberListPage from './pages/people/PrescriberListPage'
import PrescriberDetailPage from './pages/people/PrescriberDetailPage'
import NotificationsPage from './pages/NotificationsPage'
import AutoTableFunnels from './components/common/AutoTableFunnels'

function LegacyRxRedirect() {
  const { id } = useParams()
  return <Navigate to={`/rx/${id}`} replace />
}

/** D-UI-3 c4: no toast about one session survives into the next (sign-out, account or pharmacy switch). */
// Keyed on the ACTIVE pharmacy (PharmacyContext), not me.pharmacy_id: a platform admin's switch moves only
// the active pharmacy, and a sticky error about pharmacy A must not follow them into B (Phase 1 review).
function ToastIdentityGuard() {
  const { me } = useAuth()
  const { activePharmacy } = usePharmacy()
  return <ToastSessionGuard identity={`${me?.account_id ?? ''}|${activePharmacy?.id ?? me?.pharmacy_id ?? ''}`} />
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <ModulesProvider>
          <PharmacyProvider>
            <ToastIdentityGuard />
            <AppRoutes />
            {/* D-UI-2: every table that has no funnels of its own gets them, on every page. */}
            <AutoTableFunnels />
          </PharmacyProvider>
        </ModulesProvider>
      </AuthProvider>
    </ToastProvider>
  )
}

/**
 * Two route trees:
 *
 * 1. Auth routes (anonymous flows) — render WITHOUT the AppShell
 *    /login, /forgot-password, /reset-password, /accept-invitation
 *
 * 2. App routes (everything else) — render INSIDE the AppShell
 *    (sidebar + topbar + outlet). The AppShell route is the layout
 *    parent; its children are the pages.
 */
function AppRoutes() {
  // currentScreen used by FeedbackWidget — kept here so it survives
  // route changes inside the shell. Each page calls onScreenEnter()
  // to update it.
  const [currentScreen, setCurrentScreen] = useState('home')
  const enter = (s: string) => () => setCurrentScreen(s)

  // Bare product apex (owlrx.owlconsultations.com) is the PUBLIC marketing front door. Show the
  // landing page only for the root/generic-login paths; every functional route (/p/{code}/...,
  // /accept-invitation, /reset-password, /legal/*) still flows through the router below unchanged.
  if (isApexMarketingHost()) {
    const p = window.location.pathname
    // D-EMAIL-1 (round table): the marketing landing renders ONLY at the apex root. It is banned from
    // /login and every functional/deep-link path so a notification-email link can NEVER surface
    // marketing. A logged-out /login resolves to a LOGIN FORM: the user's own branded pharmacy login
    // when we remember their (non-PHI) pharmacy, otherwise the generic login (router fallthrough).
    if (p === '/') return <MarketingLanding />
    if (p === '/login') {
      let hint: string | null = null
      try { hint = localStorage.getItem('owlrx_pharmacy_hint') } catch { hint = null }
      if (hint) { window.location.replace(`/p/${hint}/login${window.location.search}`); return null }
      // no remembered pharmacy → fall through to the router's generic LoginPage (never marketing)
    }
  }

  return (
    <>
    <Routes>
      {/* Standalone auth screens — no shell. /login renders the pharmacy-branded login when the
          URL carries a pharmacy slug (under basename that's /p/{slug}/login), else the generic one. */}
      <Route path="/login"              element={resolvePharmacyFromUrl().slug
                                                   ? <PharmacyLoginPage onScreenEnter={enter('pharmacy-login')} />
                                                   : <LoginPage          onScreenEnter={enter('login')} />} />
      <Route path="/p/:slug/login"      element={<PharmacyLoginPage      onScreenEnter={enter('pharmacy-login')} />} />
      <Route path="/forgot-password"    element={<ForgotPasswordPage     onScreenEnter={enter('forgot-password')} />} />
      <Route path="/reset-password"     element={<ResetPasswordPage      onScreenEnter={enter('reset-password')} />} />
      <Route path="/accept-invitation"  element={<AcceptInvitationPage   onScreenEnter={enter('accept-invitation')} />} />

      {/* Legal / compliance — PUBLIC (reachable from login, no shell, no auth). */}
      <Route path="/legal/privacy"     element={<PrivacyPolicyPage />} />
      <Route path="/legal/terms"       element={<TermsOfUsePage />} />
      <Route path="/legal/data-rights" element={<DataRightsPage />} />
      <Route path="/legal/accessibility" element={<AccessibilityStatementPage />} />

      {/* Partner technical docs — PUBLIC like /legal/*, so a receiver spec can be shared as one link.
          Static content only: no API calls, no tenant data. */}
      <Route path="/docs/webhook-api" element={<WebhookApiDocsPage />} />

      {/* Standalone print view — renders OUTSIDE the AppShell so window.print() captures only the
          label, not the sidebar/topbar. Still fully gated: tenant context (PharmacyWorkspaceEntry)
          + shipping.create + shipping module — identical to the in-shell gating it replaces. */}
      <Route path="/shipments/:id/label" element={
        <PharmacyWorkspaceEntry>
          <RequirePerm perm="shipping.create"><RequireModule module="shipping"><LabelPrintView /></RequireModule></RequirePerm>
        </PharmacyWorkspaceEntry>
      } />


      {/* D-POT-2 — "POT classic": the standalone ASP POT app's screens rebuilt inside OwlRx on
          CareRx data. Renders OUTSIDE the AppShell so a nurse sees only the POT chrome (its own
          48px header + drawer, exactly as the app she used), with no second top bar. Still fully
          gated: tenant context (PharmacyWorkspaceEntry) + carerx.access + the carerx module + the
          per-pharmacy POT choice (owner 2026-10-20: carerx_pot_classic — exclusive with the native
          CareRx Plan of Treatment tab; a pharmacy on the OwlRx POT sees ModuleLocked here). */}
      <Route path="/pot/*" element={
        <PharmacyWorkspaceEntry>
          <RequirePerm perm="carerx.access"><RequireModule module="carerx"><RequireModule module="carerx_pot_classic"><Suspense fallback={null}><PotRoutes /></Suspense></RequireModule></RequireModule></RequirePerm>
        </PharmacyWorkspaceEntry>
      } />

      {/* D-MFR-1 — the external manufacturer portal renders OUTSIDE the pharmacy shell:
          no sidebar, no topbar, and NOT wrapped by PharmacyWorkspaceEntry. Two reasons.
          (1) An external manufacturer must see only their own drug-scoped Rx list and
          nothing else — no pharmacy navigation or chrome. (2) The shell mounts machinery
          (branding resolver, notification poll) that hits endpoints this role is not
          authorized for; leaving the portal inside the shell produced a login request
          storm. Auth and role are still fully enforced by RequireRole; the Rx scoping is
          enforced server-side by the grant resolver. */}
      <Route path="/manufacturer/*" element={
        <RequireRole role="manufacturer">
          <ManufacturerDashboard onScreenEnter={enter('dashboard:manufacturer')} />
        </RequireRole>
      } />

      {/* D-SOC-1 — the external FACILITY portal, same standalone treatment as the manufacturer
          portal above and for the same two reasons: a contracted SNF must see only its own
          residents with no pharmacy chrome, and the shell's notification poll 403s for this role
          (wrong_role), which is what produced the login loop measured on 2026-09-24.

          This route is why the portal exists at all. Until now FacilityDashboard was imported by
          nothing and routed nowhere, while the 'facility' role redirected to /provider — so a
          facility user saw the PRESCRIBER dashboard ("prescriptions you've written", Sig, Payer,
          Comments) and the Facility view builder configured a table no one could open. */}

      {/* Everything else lives inside the shell. The shell is wrapped by the pharmacy gate so the
          cross-tenant + enter-pharmacy checks run on EVERY page (passthrough on apex/admin). */}
      <Route element={<PharmacyWorkspaceEntry><AppShell currentScreen={currentScreen} /></PharmacyWorkspaceEntry>}>
        <Route path="/"          element={<HomeOrDashboard onScreenEnter={enter('home')} setScreen={setCurrentScreen} />} />
        <Route path="/p/:slug"   element={<HomeOrDashboard onScreenEnter={enter('home')} setScreen={setCurrentScreen} />} />
        <Route path="/roadmap"   element={<RequirePerm perm="platform.access"><RoadmapPage     onScreenEnter={enter('roadmap')} /></RequirePerm>} />
        <Route path="/roadmap/:id" element={<RequirePerm perm="platform.access"><RoadmapDetailPage /></RequirePerm>} />

        {/* Prescriptions — top-level, with queues as sub-tabs in the top nav.
            Sidebar holds only Home / Prescriptions / Roadmap / Admin; the
            tab strip on this page handles workflow navigation. */}
        <Route path="/rx" element={<RequirePerm perm="rx.read"><PharmacyLayout /></RequirePerm>}>
          {/* New system tabs — PM-2 + U-03 + U-04 (PR 2 of view-builder rollout).
              Order matches the Rx lifecycle from intake → ready/out, with
              Insurance and On Hold as cross-cutting blocker queues. */}
          <Route index               element={<AllRxTab />} />
          <Route path="intake"       element={<IntakeTab />} />
          <Route path="insurance"    element={<InsuranceTab />} />
          <Route path="production"   element={<ProductionTab />} />
          <Route path="ready"        element={<ReadyAndOutTab />} />
          <Route path="hold"         element={<OnHoldTab />} />
          <Route path="refills"      element={<RefillsTab />} />

          {/* Backward compat: old URLs (/rx/verify, /rx/fill, /rx/pickup)
              still resolve. Anyone with a stale bookmark or external link
              lands on the equivalent new tab. Removing these would be a
              breaking change for anyone who emailed a link. */}
          <Route path="verify"       element={<IntakeTab />} />
          <Route path="fill"         element={<ProductionTab />} />
          <Route path="pickup"       element={<ReadyAndOutTab />} />
          {/* Custom views from the view builder — dynamic route */}
          <Route path="view/:id"     element={<RequirePerm perm="queues.access"><RequireModule module="queues"><CustomViewTab /></RequireModule></RequirePerm>} />
        </Route>
        <Route path="/rx/:id" element={<RequirePerm perm="rx.read">
            <RxDetailPage />
          </RequirePerm>} />

        {/* Patient + prescriber pages */}
        <Route path="/patients" element={<RequirePerm perm="rx.read"><PatientListPage /></RequirePerm>} />
        <Route path="/patients/:id" element={<RequirePerm perm="rx.read"><PatientDetailPage /></RequirePerm>} />
        <Route path="/prescribers" element={<RequirePerm perm="rx.read"><PrescriberListPage /></RequirePerm>} />
        <Route path="/prescribers/:id" element={<RequirePerm perm="rx.read"><PrescriberDetailPage /></RequirePerm>} />

        {/* Support ticketing — pharmacy side (read_own) + SaaS queue (queue.read) */}
        <Route path="/support" element={<RequirePerm perm="support.tickets.read_own"><SupportTicketsPage /></RequirePerm>} />
        <Route path="/support/:id" element={<RequirePerm perm="support.tickets.read_own"><SupportTicketDetailPage surface="pharmacy" /></RequirePerm>} />
        <Route path="/admin/support/home" element={<RequirePerm perm="support.queue.read"><SupportHomePage /></RequirePerm>} />
        <Route path="/admin/support/monitor" element={<RequirePerm perm="support.queue.read"><SupportMonitorPage /></RequirePerm>} />
        <Route path="/admin/support/ai" element={<RequirePerm perm="support.tickets.work"><SupportAiPage /></RequirePerm>} />
        <Route path="/admin/support/settings" element={<RequirePerm perm="support.settings.manage"><SupportSettingsPage /></RequirePerm>} />
        <Route path="/admin/support/team" element={<RequirePerm perm="support.tickets.manage"><SupportTeamReportPage /></RequirePerm>} />
        <Route path="/admin/support/members" element={<RequirePerm perm="support.tickets.manage"><SupportTeamMembersPage /></RequirePerm>} />
        <Route path="/admin/support/tools" element={<RequirePerm perm="support.tickets.work"><SupportToolsPage /></RequirePerm>} />
        <Route path="/admin/support/notification-delivery" element={<RequirePerm perm="support.tickets.work"><SupportNotificationDeliveryPage /></RequirePerm>} />
        <Route path="/admin/support/pioneer" element={<RequirePerm perm="support.pioneer.read"><PioneerLookupPage /></RequirePerm>} />
        {/* Audit log for the support tier. Same component as the admin tab, reached WITHOUT the
            admin shell and with the demo-reset button off — support triages the log, it does not
            administer it. Gated on support.audit.read rather than admin.audit.read: an admin-prefixed
            permission would flip the account's legacy role bucket to admin (migration 367). */}
        <Route path="/admin/support/audit" element={<RequirePerm perm="support.audit.read"><div style={{ padding: 24 }}><h1 style={{ fontSize: 22, margin: '0 0 12px' }}>Audit log</h1><AuditExplorer /></div></RequirePerm>} />
        {/* Feedback inbox for the support tier — same component, reached without the admin shell.
            Gated on support.feedback.manage for the same reason as the audit route above. */}
        <Route path="/admin/support/feedback" element={<RequirePerm perm="support.feedback.manage"><FeedbackTab /></RequirePerm>} />
        <Route path="/admin/support" element={<RequirePerm perm="support.queue.read"><SupportQueuePage /></RequirePerm>} />
        <Route path="/admin/support/:id" element={<RequirePerm perm="support.queue.read"><SupportTicketDetailPage surface="admin" /></RequirePerm>} />
        <Route path="/admin/logs" element={<RequirePerm perm="support.logs.read"><SystemLogsPage /></RequirePerm>} />
        <Route path="/carerx" element={<RequirePerm perm="carerx.access"><RequireModule module="carerx"><CareRxPage onScreenEnter={enter('carerx')} /></RequireModule></RequirePerm>} />
        {/* D-CRX-47 POT-8: the login-scoped nurse "My visits" view — reachable by the CareRx nurse
            (provider_nurse) without rx.read; the endpoint scopes data to her own assigned visits. */}
        {/* D-POT-2: My visits is part of the native POT experience — off when the pharmacy chose POT classic. */}
        <Route path="/carerx/my-visits" element={<RequirePerm perm="carerx.access"><RequireModule module="carerx"><RequireModule module="carerx_pot_native"><CareRxPotWorklist /></RequireModule></RequireModule></RequirePerm>} />
        {/* D-POT-5 §2.3: the OwlRx Plan of Treatment form as a page (`?view=1` read-only), gated like the native tab. */}
        <Route path="/carerx/pot/form/:visitId" element={<RequirePerm perm="carerx.access"><RequireModule module="carerx"><RequireModule module="carerx_pot_native"><Suspense fallback={null}><CareRxPotFormPage /></Suspense></RequireModule></RequireModule></RequirePerm>} />
        <Route path="/secure-share" element={<RequirePerm perm="patient.docs.read"><RequireModule module="secure_share"><SecureSharePage onScreenEnter={enter('secure-share')} /></RequireModule></RequirePerm>} />
        <Route path="/carerx/dosing-calculator" element={<RequirePerm perm="calculator.access"><RequireModule module="calculator"><DosingCalculatorPage /></RequireModule></RequirePerm>} />
        <Route path="/shipments" element={<RequirePerm perm="shipping.read"><RequireModule module="shipping"><ShipmentsPage /></RequireModule></RequirePerm>} />
        <Route path="/shipments/report" element={<RequirePerm perm="shipping.report"><RequireModule module="shipping"><ShippingReportPage /></RequireModule></RequirePerm>} />
        <Route path="/shipments/global-search" element={<RequirePerm perm="shipping.global_lookup"><RequireModule module="shipping"><GlobalOrderSearchPage /></RequireModule></RequirePerm>} />
        <Route path="/shipments/courier-census" element={<RequirePerm perm="shipping.global_lookup"><RequireModule module="shipping"><CourierCensusPage /></RequireModule></RequirePerm>} />
        <Route path="/published-reports" element={<RequirePerm perm="insights.published"><RequireModule module="insights"><PharmacyReportsPage /></RequireModule></RequirePerm>} />
        {/* /shipments/:id/label moved OUT of the shell (see standalone route above) so printing excludes the chrome. */}

        {/* Notifications inbox (pharmacy and provider both use it; audience inferred from role) */}
        <Route path="/notifications" element={<NotificationsPage />} />

        {/* Dashboards */}
        <Route path="/dashboards/operations" element={<RequirePerm perm="insights.operations"><RequireModule module="insights"><OperationalDashboard /></RequireModule></RequirePerm>} />
        {/* Insights > Clinical — the Clinical dashboard (GET dashboards/clinical). The Quality / Accreditation Audit
            panes that replaced it were a mockup on seeded demo data (no API) and were removed (owner 2026-10-27:
            "any mockup or static data … delete it"). Nothing linked /dashboards/clinical-legacy, so it is gone too. */}
        <Route path="/dashboards/clinical"   element={<RequirePerm perm="insights.clinical"><RequireModule module="insights"><ClinicalDashboard /></RequireModule></RequirePerm>} />
        <Route path="/dashboards/insights"   element={<RequirePerm perm="insights.analytics"><RequireModule module="insights"><InsightsDashboard /></RequireModule></RequirePerm>} />
        <Route path="/reports/month-end" element={<RequirePerm perm="rx.read_financial"><MonthEndClosePage /></RequirePerm>} />

        {/* Reports */}
        <Route path="/reports"          element={<RequirePerm perm="insights.reports"><RequireModule module="insights"><ReportsIndex /></RequireModule></RequirePerm>} />
        <Route path="/reports/saved/:id" element={<RequirePerm perm="insights.reports"><RequireModule module="insights"><SavedReportDetail /></RequireModule></RequirePerm>} />
        <Route path="/reports/pa-by-drug" element={<RequirePerm perm="insights.reports"><RequireModule module="insights"><PaByDrugReport /></RequireModule></RequirePerm>} />
        <Route path="/reports/:slug"    element={<RequirePerm perm="insights.reports"><RequireModule module="insights"><ReportDetail /></RequireModule></RequirePerm>} />

        {/* Legacy /pharmacy URLs — keep redirects for any in-flight links/bookmarks */}
        <Route path="/pharmacy"          element={<Navigate to="/rx" replace />} />
        <Route path="/pharmacy/queue"    element={<Navigate to="/rx" replace />} />
        <Route path="/pharmacy/verify"   element={<Navigate to="/rx/verify" replace />} />
        <Route path="/pharmacy/fill"     element={<Navigate to="/rx/fill" replace />} />
        <Route path="/pharmacy/pickup"   element={<Navigate to="/rx/pickup" replace />} />
        <Route path="/pharmacy/refills"  element={<Navigate to="/rx/refills" replace />} />
        {/* <Navigate to="/rx/:id"> sends the LITERAL ":id" — React Router does not
            substitute params in a static `to`. Every legacy deep link on this path
            has been landing on a 404. Resolve the param explicitly. */}
        <Route path="/pharmacy/rx/:id"   element={<LegacyRxRedirect />} />
        <Route path="/provider/patients/:id" element={<RequireRole role={['provider','provider_nurse']}>
            <ProviderPatientPage />
          </RequireRole>} />
        {/* D-MFR-1 — the manufacturer portal is NOT here: it renders as a standalone
            route OUTSIDE this pharmacy-shell wrapper (see above, near the auth routes),
            so it carries no sidebar, topbar, or pharmacy workspace machinery. */}

        {/* D-SOC-3 (Patrick 2026-09-05) put Facility Staff on the PROVIDER portal — "no second
            shell to build". D-SOC-1 had already built one, and the Facility view builder
            (D-SOC-8) configures THAT table, so the two designs contradicted each other and
            D-SOC-3 won by routing. Mario chose the standalone portal on 2026-09-25, so
            'facility' comes off these routes; its portal is the standalone /facility/* above.

            This is what made the provider table appear inside the Facility Portal shell, and it
            is why a facility user hit `module_access_denied` for 'drugreference.access':
            ProviderDashboard calls /drug-reference/for-rx-batch, an endpoint no facility role
            can ever reach. Removing the role from the route removes the call.

            D-SOC-9 S1 then moved the facility portal INSIDE this shell (below), so it keeps the
            nav — what it must never do is come back to THESE routes. */}

        <Route path="/provider/*" element={<RequireRole role={['provider','provider_nurse']}>
            <ProviderDashboard onScreenEnter={enter('dashboard:provider')} />
          </RequireRole>} />
        {/* D-SOC-9 S2/S3 — the facility portal is the SAME component the provider portal uses,
            with its data source injected (portal="facility"). Mario, 2026-09-25: "i need to see
            same tables as provider portal but in the facility with the customizations" — and,
            bluntly, "it was great when it was shared, now it's trash". S1 shipped a stripped-down
            page with two placeholder tabs, which was a downgrade. This restores the real thing:
            three working tabs, the full column set, filters, and per-pharmacy customisation from
            the SaaS admin (fac_rx_list / fac_pt_list / fac_home), including PioneerRx-mapped
            columns, which arrive through batchCustomFieldValues keyed on the Rx id and are
            therefore portal-agnostic.

            It is NOT on /provider (D-SOC-3, reversed in #2713): the routes stay separate so a
            facility never reaches provider-only endpoints it would be 403'd on. Mario, 2026-09-25: "i need the portal layout with the
            nav bar on the left and the tabs".

            It is NOT on /provider (that was D-SOC-3, reversed in #2713): ProviderDashboard calls
            the module-gated drug-reference endpoint, which no facility role can hold, and its
            framing is the prescriber's. Same shell, facility content, facility APIs.

            The shell's notification poll is safe for this role: #2708 added 'facility' to the
            four RequireOneOf calls in NotificationFunctions, and each read is bound to
            `WHERE account_id=@a`, so admitting the role widens no data. Without that fix this
            route would reinstate the ~940-request login loop measured on 2026-09-24. */}
        <Route path="/facility" element={<RequireRole role="facility">
            <ProviderDashboard onScreenEnter={enter('dashboard:facility')} portal="facility" />
          </RequireRole>} />
        <Route path="/facility/prescriptions" element={<RequireRole role="facility">
            <ProviderDashboard onScreenEnter={enter('dashboard:facility')} portal="facility" />
          </RequireRole>} />
        <Route path="/facility/residents" element={<RequireRole role="facility">
            <ProviderDashboard onScreenEnter={enter('dashboard:facility')} portal="facility" />
          </RequireRole>} />
        <Route path="/patient/*" element={<RequireRole role="patient">
            <PatientDashboard onScreenEnter={enter('dashboard:patient')} />
          </RequireRole>} />
        {/* D-SUPPORT-3: support tier shares the admin shell for monitoring surfaces; sensitive
            children stay protected by their own admin.* API permissions (nav also hides them). */}
        <Route path="/admin" element={<RequireRole role={['admin', 'support']}><AdminLayout /></RequireRole>}>
          <Route index element={<RequireMode mode="saas" fallback={<Navigate to="users" replace />}><SystemMonitor /></RequireMode>} />
          {/* Pharmacy-scoped admin tools (work in both modes; tenant-scoped at API) */}
          <Route path="users/*"        element={<UsersTab />} />
          <Route path="roles/*"        element={<RolesTab />} />
          <Route path="groups/*"       element={<GroupsTab />} />
          <Route path="invitations"    element={<InvitationsTab />} />
          {/* SaaS-only admin tools (WI-900). Pharmacy Owners must NOT
              reach these even though they have admin.* permissions —
              they're cross-tenant configuration that belongs to Owl. */}
          {/* Audit log: visible in BOTH modes. D-AUDIT-2: one pharmacy at a time, always. A bound
              admin sees their own pharmacy; a SaaS admin picks one pharmacy, or Platform. */}
          <Route path="audit"          element={<RequirePerm perm="admin.audit.read"><div style={{ padding: 24 }}><h1 style={{ fontSize: 22, margin: '0 0 12px' }}>Audit log</h1><AuditExplorer allowPlatform toolbarExtra={<DemoResetControl />} /></div></RequirePerm>} />
          <Route path="data-requests"  element={<DsrTab />} />
          <Route path="feedback"       element={<RequireMode mode="saas"><FeedbackTab /></RequireMode>} />
          <Route path="pioneerrx"      element={<RequireMode mode="pharmacy"><PioneerRxTab /></RequireMode>} />
          <Route path="pharmacies"     element={<RequireMode mode="saas"><PharmaciesTab /></RequireMode>} />
          <Route path="sso"            element={<RequireMode mode="saas"><PlatformSsoConfig /></RequireMode>} />
          <Route path="email"          element={<RequireMode mode="saas"><PlatformEmailConfig /></RequireMode>} />
          <Route path="pharmacies/new" element={<RequireMode mode="saas"><PharmacyOnboardingWizard /></RequireMode>} />
          <Route path="pharmacies/:id" element={<RequireMode mode="saas"><PharmacyDetailPage /></RequireMode>} />
          <Route path="overview"       element={<RequireMode mode="saas"><AdminDashboard onScreenEnter={enter('dashboard:admin')} /></RequireMode>} />
          <Route path="api-keys"       element={<RequireMode mode="saas"><ApiKeysAdmin /></RequireMode>} />
          <Route path="network-security" element={<RequireMode mode="saas"><NetworkSecurityTab /></RequireMode>} />
          {/* Ops surface for the list-scoped carrier-order backfill. Gated on
              shipping.backfill; the endpoint additionally requires admin server-side,
              so the UI gate mirrors rather than replaces the real check. */}
          <Route path="carrier-order-import" element={<RequirePerm perm="shipping.backfill"><CarrierOrderImportPage /></RequirePerm>} />
          <Route path="latency"        element={<RequireMode mode="saas"><LatencyBoard /></RequireMode>} />
          <Route path="security"        element={<RequireMode mode="saas"><SecurityDashboardTab /></RequireMode>} />
          <Route path="compliance"     element={<RequireMode mode="saas"><ComplianceTab /></RequireMode>} />
          <Route path="cost"           element={<RequireMode mode="saas"><CostTab /></RequireMode>} />
          <Route path="profitability"  element={<RequireMode mode="saas"><ProfitabilityPage /></RequireMode>} />
          {/* D-COST-4: background worker schedule + working window. SaaS-admin only — the PUT is
              additionally role-gated server-side, so a pharmacy-bound actor cannot reach it. */}
          <Route path="workers"        element={<RequireMode mode="saas"><WorkersTab /></RequireMode>} />
          <Route path="resources"      element={<RequireMode mode="saas"><ResourcesTab /></RequireMode>} />
          <Route path="api-config"     element={<RequireMode mode="saas"><ApiConfigTab /></RequireMode>} />
          <Route path="data-api"       element={<RequireMode mode="saas"><DataApiGovernanceTab /></RequireMode>} />
          <Route path="reports"        element={<RequireMode mode="saas"><ReportBuilderPage /></RequireMode>} />
          <Route path="reports-ai"     element={<RequireMode mode="saas"><ReportBuilderSqlPage /></RequireMode>} />
          <Route path="reports-ai-prompt" element={<RequireMode mode="saas"><ReportAiPromptPage /></RequireMode>} />
          <Route path="rx-view"        element={<RequireMode mode="saas"><RxViewBuilderPage /></RequireMode>} />
          <Route path="home-view"      element={<RequireMode mode="saas"><RxViewBuilderPage surface="home" /></RequireMode>} />
          <Route path="patient-view"   element={<RequireMode mode="saas"><RxViewBuilderPage surface="patient" /></RequireMode>} />
          <Route path="prescriber-view" element={<RequireMode mode="saas"><RxViewBuilderPage surface="prescriber" /></RequireMode>} />
          {/* D-SOC-8 — the FACILITY portal's prescription table. Its catalogue is a fixed safe
              allow-list, not the full Rx field set: this surface configures what an EXTERNAL
              party sees, so an admin reorders and hides but can never add a field the server
              does not already return. */}
          <Route path="facility-view"  element={<RequireMode mode="saas"><RxViewBuilderPage surface="facility" /></RequireMode>} />
        </Route>

        <Route path="/settings/pharmacy" element={<RequirePerm perm="pharmacy.self.manage"><PharmacySettingsHub /></RequirePerm>} />
        <Route path="/security" element={<Navigate to="/settings/pharmacy?tab=security" replace />} />
        <Route path="/carerx/esign-config" element={<EsignConfigPage />} />
        <Route path="/settings/notifications" element={<NotificationSettings />} />
        {/* D-RBAC-1 — "Users and groups": Users + Groups tabs on one pharmacy-side page (a bound admin never sees a pharmacy picker). */}
        <Route path="/manage-users" element={<RequirePerm perm="admin.users.read"><UsersAndGroupsPage /></RequirePerm>} />
        <Route path="/manage-groups" element={<Navigate to="/manage-users?tab=groups" replace />} />
        {/* Legacy routes redirect to hub */}
        <Route path="/settings/audit" element={<RequirePerm perm="pharmacy.self.manage"><PharmacySettingsHub /></RequirePerm>} />
        <Route path="/settings/views" element={<RequirePerm perm="pharmacy.self.manage"><PharmacySettingsHub /></RequirePerm>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    <RecordingWidget />
    </>
  )
}

/**
 * Root "/" handler. Routing rules:
 *   1. Anonymous              → redirected to /login
 *   2. Pharmacy mode          → role-appropriate pharmacy dashboard
 *                               (OwnerHome for staff/admin in pharmacy,
 *                                ProviderDashboard, PatientDashboard)
 *   3. SaaS mode (admin only) → SaasHome (platform health, no Rx data)
 *
 * The mode signal comes from PharmacyContext (WI-893): mode is 'pharmacy'
 * when the user has a home pharmacy OR a SaaS admin entered one;
 * otherwise 'saas'.
 */
function HomeOrDashboard({
  setScreen,
}: { onScreenEnter?: () => void; setScreen: (s: string) => void }) {
  const { me, role, loading, hasPermission } = useAuth()
  const { mode } = usePharmacy()
  // Module entitlement for the provider_nurse landing below — the SAME hook RequireModule reads, so
  // the redirect target and the route guard can never disagree (no new fetch).
  const { known: modulesKnown, isDisabled: moduleDisabled } = useModules()
  if (loading) return null
  // Unauthenticated visitors to the app root go to the sign-in screen, not the
  // in-development mockup landing (which was surfacing when admins hit the portal
  // while logged out). The public marketing page still renders at the apex host root.
  if (!me?.authenticated) return <RedirectToLogin />

  const enterAs = (s: string) => () => setScreen(`dashboard:${s}`)

  // D-MFR-1 — a manufacturer is a pharmacy-BOUND external role, so mode resolves to
  // 'pharmacy' and this component would otherwise render its dashboard INSIDE the
  // pharmacy shell. That shell polls GET /api/notifications, which 403s for the
  // manufacturer role (wrong_role), and the login→home transition turned that into a
  // request storm (a HAR showed 233 notification 403s and 601 branding refetches in
  // ~15s). The manufacturer has its own shell at /manufacturer/*; send it there so it
  // never mounts the pharmacy chrome at all. Redirect, not inline render, so there is
  // exactly one place the manufacturer UI lives.
  if (role === 'manufacturer') return <Navigate to="/manufacturer" replace />

  // D-SOC-1 — a FACILITY account is the same shape of external, pharmacy-BOUND role, and it hit
  // the identical storm: mode resolves to 'pharmacy', this component renders the pharmacy shell,
  // the shell polls GET /api/notifications which 403s wrong_role for 'facility', and the
  // login -> home -> rejected -> login cycle turns that into a loop. Measured in production on
  // 2026-09-24: ~940 requests and climbing, page stuck on "Loading pharmacy...".
  //
  // The first fix sent them to /provider because that stopped the loop, but /provider renders
  // ProviderDashboard — the PRESCRIBER's table. A facility user saw "prescriptions you've
  // written" with Sig/Payer/Comments, and FacilityDashboard was routed nowhere at all. The
  // portal is /facility (standalone, above); the loop stays fixed because that route never
  // mounts the pharmacy chrome either.
  if (role === 'facility') return <Navigate to="/facility" replace />


  // SaaS mode: platform overview, NOT pharmacy data. Anyone with admin
  // role lands here when no pharmacy is active.
  if (mode === 'saas') {
    if (role === 'admin') return <SaasHome onScreenEnter={enterAs('saas')} />
    // D-SUPPORT-2 D4: platform support tier — their home IS the support queue. Sending
    // them to login (the old "degenerate state" branch) looped: authenticated users
    // bounce off /login back to /, which sent them to /login again, forever.
    if (role === 'support') return <Navigate to="/admin/support/home" replace />
    // Any OTHER non-admin platform account remains a degenerate state (shouldn't
    // happen — pharmacy users always have me.pharmacy_id and thus pharmacy mode).
    // Send to login rather than show the dev mockup landing or pharmacy data.
    return <RedirectToLogin />
  }

  // Pharmacy mode: role-specific dashboard
  switch (role) {
    case 'pharmacy_staff': return <OwnerHome onScreenEnter={enterAs('pharmacy_staff')} />
    case 'provider':       return <ProviderDashboard onScreenEnter={enterAs('provider')} />
    // D-POT-2 R20 — a CareRx nurse works in her pharmacy's Plan of Treatment experience, not on the
    // PRESCRIBER's practice dashboard ("219 active prescriptions of 222 you have written" is the
    // prescriber's view and is wrong for a nurse). Redirect, not inline render, so the nurse UI lives
    // in exactly one place. Owner 2026-10-20: the two POT experiences are an EXCLUSIVE per-pharmacy
    // choice (carerx_pot_classic is the source of truth; /me/modules derives native = NOT classic).
    //   carerx.access + carerx on + classic chosen → /pot/dashboard   (POT classic, outside the shell)
    //   carerx.access + carerx on + native         → /carerx?tab=pot  (native Plan of Treatment tab)
    //   carerx.access, carerx known-disabled       → /carerx/my-visits (RequireModule shows ModuleLocked)
    //   no carerx.access                           → the existing provider dashboard (unchanged)
    // No loop: every target is gated by RequirePerm('carerx.access') + RequireModule(...), which read
    // the SAME hasPermission / useModules values checked here; no target ever navigates back to "/".
    // While entitlement is still resolving we render nothing (as RequireModule does) rather than
    // flash the prescriber dashboard or redirect prematurely.
    case 'provider_nurse': {
      if (!hasPermission('carerx.access')) return <ProviderDashboard onScreenEnter={enterAs('provider')} />
      if (!modulesKnown) return null
      // POT off for the pharmacy (both experiences off, owner 2026-10-26) → the same ModuleLocked page.
      if (moduleDisabled('carerx') || (moduleDisabled('carerx_pot_classic') && moduleDisabled('carerx_pot_native'))) return <Navigate to="/carerx/my-visits" replace />
      return <Navigate to={moduleDisabled('carerx_pot_classic') ? '/carerx?tab=pot' : '/pot/dashboard'} replace />
    }
    case 'patient':        return <PatientDashboard  onScreenEnter={enterAs('patient')} />
    case 'admin':          return <OwnerHome onScreenEnter={enterAs('admin')} />
    // 'manufacturer' is handled by the early redirect above — it never reaches this
    // switch, so it must not render inside the pharmacy shell here.
    default:               return <RedirectToLogin />
  }
}

/**
 * Redirects an unauthenticated user to /login while REMEMBERING where they were headed (path +
 * query), so an email deep-link like /secure-share?doc=… isn't lost: after login, LoginPage reads
 * location.state.from and sends them on to the document instead of a generic home.
 */
function RedirectToLogin() {
  const location = useLocation()
  // NEXT-1: ALSO carry the return path in the query string — router state dies at the
  // window.location.replace hard redirects in the login resolver chain; ?next survives them,
  // so a session expiry on e.g. /p/{code}/rx/view/{id} logs back in to that same tab.
  const next = encodeURIComponent(location.pathname + location.search)
  return <Navigate to={`/login?next=${next}`} state={{ from: location }} replace />
}

/**
 * Tiny route guard. Anonymous → /login. Wrong role → /. Otherwise renders.
 */
function RequireRole({ role, children }: { role: 'pharmacy_staff' | 'provider' | 'patient' | 'admin' | 'provider_nurse' | 'manufacturer' | 'support' | 'facility' | Array<'pharmacy_staff' | 'provider' | 'patient' | 'admin' | 'provider_nurse' | 'manufacturer' | 'support' | 'facility'>; children: React.ReactNode }) {
  const { me, role: actorRole, loading } = useAuth()
  if (loading) return null
  if (!me?.authenticated) return <RedirectToLogin />
  const allowed = Array.isArray(role) ? role : [role]
  if (!actorRole || !allowed.includes(actorRole as any)) return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * Mode-aware route guard (WI-900). Some routes (the SaaS administration
 * tabs — Pharmacies onboarding, system audit, feedback, PioneerRx,
 * API keys) are reachable ONLY when the user is in SaaS mode. A
 * pharmacy Owner who has admin.* perms must NOT see these — they're
 * cross-tenant configuration that belongs to the SaaS operator.
 *
 * Usage:
 *   <RequireMode mode="saas"><PharmaciesTab /></RequireMode>
 *
 * Anonymous → /login. Wrong mode → /. Otherwise renders.
 */
function RequireMode({ mode: required, children, fallback }: { mode: 'saas' | 'pharmacy'; children: React.ReactNode; fallback?: React.ReactNode }) {
  const { me, loading } = useAuth()
  const { mode: actorMode } = usePharmacy()
  if (loading) return null
  if (!me?.authenticated) return <RedirectToLogin />
  if (actorMode !== required) return <>{fallback ?? <Navigate to="/" replace />}</>
  return <>{children}</>
}

/**
 * Permission-based route guard. Anonymous → /login. Missing permission → /.
 */
function RequirePerm({ perm, children }: { perm: string; children: React.ReactNode }) {
  const { me, hasPermission, loading } = useAuth()
  if (loading) return null
  if (!me?.authenticated) {
    // Bare /login: under a /p/{slug} basename the router prefixes it to /p/{slug}/login, so the
    // pharmacy context is preserved; on apex/admin (no basename) it stays the generic login.
    return <RedirectToLogin />
  }
  if (!hasPermission(perm)) return <Navigate to="/" replace />
  return <>{children}</>
}

// ADR-019 D-9: route-level module gate. Renders children unless the module is KNOWN disabled for
// this pharmacy, in which case it shows a friendly notice instead of letting the page's API calls
// 403. While entitlement is still resolving (known=false) it renders nothing briefly.
function RequireModule({ module, children }: { module: string; children: React.ReactNode }) {
  const { me, loading } = useAuth()
  const { known, isDisabled } = useModules()
  if (loading) return null
  if (!me?.authenticated) return <RedirectToLogin />
  if (!known) return null
  if (isDisabled(module)) return <ModuleLocked moduleKey={module} />
  return <>{children}</>
}

